/**
 * Osservabilità runtime dell'app.
 *
 * Tutto ciò che serve per capire cosa sta succedendo senza aprire i DevTools:
 * - eventi (errori, caricamenti, navigazioni) in un ring buffer;
 * - durata di ogni query analytics (count, ultima, massima, totale) e `performance.measure`;
 * - registro dei grafici con serie, punti e stato di rendering;
 * - snapshot completo su `window.__money.snapshot()`.
 *
 * In più lo stato di alto livello è riflesso su `<html data-money-*>`, così test e script
 * possono aspettare condizioni precise invece di usare timeout.
 *
 * Il modulo non dipende da Svelte né dal DOM per le parti pure, quindi è testabile in Node.
 */

export type TelemetryLevel = 'error' | 'warn' | 'info';

export interface TelemetryEvent {
  at: number;
  level: TelemetryLevel;
  type: string;
  message: string;
  detail?: unknown;
}

export interface MeasureStat {
  count: number;
  lastMs: number;
  maxMs: number;
  totalMs: number;
}

export interface ChartSeriesSnapshot {
  name: string;
  type: string;
  points: number;
  visible: boolean;
  values: unknown[];
}

export interface ChartSnapshot {
  id: string;
  label: string;
  status: 'pending' | 'initializing' | 'rendered' | 'error';
  categories: string[];
  series: ChartSeriesSnapshot[];
  renders: number;
  error?: string;
}

const MAX_EVENTS = 300;
const events: TelemetryEvent[] = [];
const measures = new Map<string, MeasureStat>();
const charts = new Map<string, ChartSnapshot>();
let stateProvider: (() => unknown) | undefined;
let chartSequence = 0;

function now(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

export function describeError(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return error.stack
      ? { message: error.message, stack: error.stack }
      : { message: error.message };
  }
  return { message: typeof error === 'string' ? error : JSON.stringify(error) };
}

export function record(
  level: TelemetryLevel,
  type: string,
  message: string,
  detail?: unknown,
): void {
  events.push({
    at: Math.round(now()),
    level,
    type,
    message,
    ...(detail === undefined ? {} : { detail }),
  });
  if (events.length > MAX_EVENTS) events.splice(0, events.length - MAX_EVENTS);
  if (level === 'error' && typeof document !== 'undefined') {
    document.documentElement.dataset.moneyErrors = String(
      events.filter((event) => event.level === 'error').length,
    );
  }
}

export function recordError(type: string, error: unknown, detail?: Record<string, unknown>): void {
  const described = describeError(error);
  record('error', type, described.message, {
    ...detail,
    ...(described.stack ? { stack: described.stack } : {}),
  });
}

/** Esegue `fn` misurandone la durata sotto `name`. Le eccezioni vengono propagate. */
export function measure<T>(name: string, fn: () => T): T {
  const start = now();
  try {
    return fn();
  } finally {
    const duration = now() - start;
    const stat = measures.get(name) ?? { count: 0, lastMs: 0, maxMs: 0, totalMs: 0 };
    stat.count += 1;
    stat.lastMs = duration;
    stat.maxMs = Math.max(stat.maxMs, duration);
    stat.totalMs += duration;
    measures.set(name, stat);
    if (typeof performance !== 'undefined' && typeof performance.measure === 'function') {
      try {
        performance.measure(`money:${name}`, { start, duration });
      } catch {
        // Alcuni ambienti (jsdom) non supportano la forma con opzioni: la statistica resta.
      }
    }
  }
}

/** Avvolge una funzione pura in modo che ogni chiamata venga misurata. */
export function measured<A extends unknown[], R>(name: string, fn: (...args: A) => R) {
  return (...args: A): R => measure(name, () => fn(...args));
}

export function nextChartId(): string {
  chartSequence += 1;
  return `chart-${chartSequence}`;
}

export function updateChart(snapshot: ChartSnapshot): void {
  charts.set(snapshot.id, snapshot);
}

export function removeChart(id: string): void {
  charts.delete(id);
}

/** Estrae da un'opzione ECharts ciò che serve per verificare cosa è disegnato. */
export function summarizeChartOption(
  option: unknown,
  hiddenSeries: ReadonlySet<string> = new Set(),
): Pick<ChartSnapshot, 'categories' | 'series'> {
  const record = (option ?? {}) as Record<string, unknown>;
  const axes = ([] as unknown[]).concat(record.xAxis ?? [], record.yAxis ?? []);
  const categoryAxis = axes.find(
    (axis) =>
      !!axis &&
      typeof axis === 'object' &&
      (axis as Record<string, unknown>).type === 'category' &&
      Array.isArray((axis as Record<string, unknown>).data),
  ) as Record<string, unknown> | undefined;
  const categories = ((categoryAxis?.data as unknown[]) ?? []).map((item) =>
    item && typeof item === 'object' && 'value' in item
      ? String((item as { value: unknown }).value)
      : String(item),
  );
  const rawSeries = Array.isArray(record.series)
    ? record.series
    : record.series
      ? [record.series]
      : [];
  const series = rawSeries.flatMap((raw): ChartSeriesSnapshot[] => {
    if (!raw || typeof raw !== 'object') return [];
    const item = raw as Record<string, unknown>;
    const data = Array.isArray(item.data) ? item.data : [];
    const name = typeof item.name === 'string' ? item.name : String(item.id ?? '');
    return [
      {
        name,
        type: typeof item.type === 'string' ? item.type : 'unknown',
        points: data.length,
        visible: !hiddenSeries.has(name),
        values: data.map((datum) =>
          datum && typeof datum === 'object' && 'value' in datum
            ? (datum as { value: unknown }).value
            : datum,
        ),
      },
    ];
  });
  return { categories, series };
}

export function setAppAttribute(name: string, value: string | null): void {
  if (typeof document === 'undefined') return;
  const key = `money${name.charAt(0).toUpperCase()}${name.slice(1)}`;
  if (value === null) delete document.documentElement.dataset[key];
  else document.documentElement.dataset[key] = value;
}

export function telemetrySnapshot() {
  return {
    state: stateProvider?.(),
    events: events.map((event) => ({ ...event })),
    errors: events.filter((event) => event.level === 'error'),
    measures: Object.fromEntries(
      Array.from(measures, ([name, stat]) => [
        name,
        {
          count: stat.count,
          lastMs: Number(stat.lastMs.toFixed(3)),
          maxMs: Number(stat.maxMs.toFixed(3)),
          avgMs: Number((stat.totalMs / stat.count).toFixed(3)),
        },
      ]),
    ),
    charts: Array.from(charts.values()).map((chart) => ({
      ...chart,
      series: chart.series.map((series) => ({ ...series })),
    })),
  };
}

export type TelemetrySnapshot = ReturnType<typeof telemetrySnapshot>;

export function resetTelemetry(): void {
  events.length = 0;
  measures.clear();
  charts.clear();
  chartSequence = 0;
  stateProvider = undefined;
}

declare global {
  interface Window {
    __money?: {
      snapshot: typeof telemetrySnapshot;
      resetMeasures: () => void;
    };
  }
}

/** Installa gli handler globali e l'API `window.__money`. Idempotente. */
export function installTelemetry(provider: () => unknown): void {
  stateProvider = provider;
  if (typeof window === 'undefined' || window.__money) return;
  window.__money = { snapshot: telemetrySnapshot, resetMeasures: () => measures.clear() };
  window.addEventListener('error', (event) =>
    recordError('window.error', event.error ?? event.message, {
      source: event.filename,
      line: event.lineno,
    }),
  );
  window.addEventListener('unhandledrejection', (event) =>
    recordError('unhandledrejection', event.reason),
  );
}
